/**
 * The extra data a few TLDs require of the registrant (owner rule 2026-09-30: every TLD is handled
 * alike; only some unusual ones ask for more, e.g. ".us" asks how the registrant is connected to
 * the United States). Labels, help and the accepted choices come from the server in the page's
 * language; nothing here knows a TLD.
 *
 * - `useTldExtraFields` + `ExtraFieldInputs`: the fields of an order form (registration), with
 *   what fills a field left empty for the chosen registrant, and each field's refusal message;
 * - `DomainExtraFieldsSection`: the domain's values on "Chủ thể & liên hệ" (personal data only
 *   masked), and staff's edit (reason required).
 */

import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api, isApiError, type DomainDetail, type RegistrantProblem } from '../../api';
import type { DomainExtraFieldValue, ExtraFieldDef, TldExtraFields } from '../../api/areas/core';
import { Callout, Field, Section } from '../../components/blocks';
import { useWrite, WriteError } from '../../components/Write';
import { countryOptions } from '../../lib/countries';
import { changedValues, extraFieldErrors } from '../../lib/extraFields';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';

const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30';

type FormField = TldExtraFields['fields'][number];

/** The fields `zone` asks of registrant `subjectId` (all fields without one); `null` while unknown or when the zone asks nothing. */
export function useTldExtraFields(zone: string | null, subjectId: string): TldExtraFields | null {
  const { i18n } = useTranslation();
  const r = useApi(
    (signal) => (zone ? api.getTldExtraFields(zone, { lang: i18n.language, subject_id: subjectId || undefined }, { signal }) : Promise.resolve(null)),
    [zone, subjectId, i18n.language],
  );
  return r.status === 'success' && r.data && r.data.fields.length ? r.data : null;
}

/** One input of the right kind for a field. */
function FieldControl({ f, value, onChange, disabled, placeholder }: { f: ExtraFieldDef; value: string; onChange: (v: string) => void; disabled?: boolean; placeholder?: string }) {
  const { t, i18n } = useTranslation('domains');
  if (f.type === 'select' || f.type === 'boolean' || f.type === 'country') {
    const options: [string, string][] =
      f.type === 'select' ? f.values.map((v) => [v.value, v.label]) : f.type === 'boolean' ? [['true', t('extra.yes')], ['false', t('extra.no')]] : countryOptions(i18n.language);
    return (
      <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-extra-field={f.key}>
        <option value="">{placeholder ?? t('extra.choose')}</option>
        {options.map(([v, label]) => (
          <option key={v} value={v}>
            {label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <Input
      type={f.type === 'date' ? 'date' : 'text'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      maxLength={f.max_length ?? 500}
      disabled={disabled}
      placeholder={placeholder}
      autoComplete="off"
      data-extra-field={f.key}
    />
  );
}

/** What fills a field left empty, in words. */
function prefillNote(f: FormField, zone: string, t: (k: string, o?: Record<string, unknown>) => string): string | null {
  const p = f.prefill;
  if (!p) return null;
  if (p.source === 'EARLIER') return p.hint ? t('extra.fromEarlierHidden', { hint: p.hint }) : t('extra.fromEarlier', { zone });
  return p.hint ? t('extra.fromSubject', { hint: p.hint }) : t('extra.fromSubjectPlain');
}

/**
 * The extra fields of an order form under their heading ("Thông tin bổ sung theo yêu cầu của đuôi
 * .us"): `values` as typed, `errors` from a refusal (`REGISTRANT_INCOMPLETE`, field `extra.<key>`).
 */
export function ExtraFieldInputs({
  spec,
  values,
  onChange,
  problems,
}: {
  spec: TldExtraFields;
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  problems?: RegistrantProblem[];
}) {
  const { t } = useTranslation('domains');
  const tr = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const errors = extraFieldErrors(problems ?? []);
  return (
    <fieldset className="grid gap-3 rounded-md border p-3" data-extra-fields={spec.zone}>
      <legend className="px-1 text-sm font-medium">{t('extra.heading', { zone: spec.zone })}</legend>
      <p className="text-xs text-muted-foreground">{t('extra.intro')}</p>
      {spec.fields.map((f) => {
        const note = prefillNote(f, spec.zone, tr);
        // Before a registrant is chosen, for whom the field is required.
        const need =
          f.required_now === true
            ? t('extra.required')
            : f.required_now === false || f.required === 'optional'
              ? t('extra.optional')
              : f.required === 'individuals'
                ? t('extra.requiredIndividuals')
                : f.required === 'organizations'
                  ? t('extra.requiredOrganizations')
                  : t('extra.required');
        return (
          <div key={f.key} className="grid gap-1">
            <Field
              label={
                <>
                  {f.label} <span className="font-normal">({need})</span>
                </>
              }
              hint={[f.help, note].filter(Boolean).join(' ') || undefined}
            >
              <FieldControl f={f} value={values[f.key] ?? ''} onChange={(v) => onChange(f.key, v)} placeholder={f.sensitive && f.prefill?.hint ? f.prefill.hint : undefined} />
            </Field>
            {errors[f.key] ? (
              <p className="text-xs text-critical" role="alert" data-extra-error={f.key}>
                {errors[f.key]}
              </p>
            ) : null}
          </div>
        );
      })}
    </fieldset>
  );
}

/** A stored value as shown: a code's label, a masked hint, or "Chưa có". */
function shownValue(f: DomainExtraFieldValue, t: (k: string) => string): string {
  if (!f.is_set) return t('extra.notSet');
  if (f.sensitive) return `${f.hint ?? '••••'} (${t('extra.hidden')})`;
  return f.value_label ?? f.value ?? t('extra.notSet');
}

function EditDialog({ detail, fields, zone, onClose, onChanged }: { detail: DomainDetail; fields: DomainExtraFieldValue[]; zone: string; onClose: () => void; onChanged: () => void }) {
  const { t, i18n } = useTranslation('domains');
  const d = detail.domain;
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(fields.map((f) => [f.key, f.sensitive ? '' : (f.value ?? '')])));
  const [reason, setReason] = useState('');
  const w = useWrite((b: { values: Record<string, string | null>; reason: string; lang: string }, opts) => api.editDomainExtraFields(d.id, b, opts));
  const changes = changedValues(fields, values);
  const error = w.state.phase === 'error' ? w.state.error : null;
  const refused = isApiError(error) && error.code === 'REGISTRANT_INCOMPLETE' ? ((error.details as { fields?: RegistrantProblem[] } | null)?.fields ?? []) : [];
  const errors = extraFieldErrors(refused);
  const done = w.state.phase === 'done';
  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('extra.editTitle', { domain: d.unicode_name })}</DialogTitle>
          <DialogDescription>{t('extra.editDescription')}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void w.run({ values: changes, reason: reason.trim(), lang: i18n.language }).then((r) => r && onChanged());
          }}
        >
          <p className="text-sm font-medium">{t('extra.heading', { zone })}</p>
          {fields.map((f) => (
            <div key={f.key} className="grid gap-1">
              <Field label={f.label} hint={f.sensitive && f.is_set ? t('extra.keepHidden') : (f.help ?? undefined)}>
                <FieldControl
                  f={f}
                  value={values[f.key] ?? ''}
                  onChange={(v) => setValues((x) => ({ ...x, [f.key]: v }))}
                  disabled={done}
                  placeholder={f.sensitive && f.hint ? f.hint : undefined}
                />
              </Field>
              {errors[f.key] ? (
                <p className="text-xs text-critical" role="alert" data-extra-error={f.key}>
                  {errors[f.key]}
                </p>
              ) : null}
            </div>
          ))}
          <Field label={t('actions.reason')} hint={t('actions.reasonHint')}>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} required disabled={done} />
          </Field>
          {error && !refused.length ? <WriteError error={error} /> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('actions.close')}
            </Button>
            <Button type="submit" disabled={w.busy || done || !reason.trim() || Object.keys(changes).length === 0}>
              {t('extra.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Thông tin bổ sung theo yêu cầu của đuôi .us" on the domain page: each field with its value
 * (personal data masked), where it came from and when; "Sửa" for staff. Nothing for a TLD that
 * asks nothing.
 */
export function DomainExtraFieldsSection({ detail, onChanged }: { detail: DomainDetail; onChanged?: () => void }) {
  const { t, i18n } = useTranslation('domains');
  const tr = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const d = detail.domain;
  const [editing, setEditing] = useState(false);
  const r = useApi((signal) => api.getDomainExtraFields(d.id, { lang: i18n.language }, { signal }), [d.id, d.version, i18n.language]);
  if (r.status !== 'success' || !r.data.fields.length || !r.data.zone) {
    return r.status === 'error' ? <Callout tone="warn">{t('extra.loadFailed')}</Callout> : null;
  }
  const { zone, fields } = r.data;
  return (
    <div id="extra-fields" className="scroll-mt-20">
      <Section
        title={t('extra.heading', { zone })}
        description={t('extra.sectionHint')}
        actions={
          <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)} data-extra-edit>
            <Pencil /> {t('extra.edit')}
          </Button>
        }
      >
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          {fields.map((f) => (
            <div key={f.key} className="min-w-0" data-extra-value={f.key}>
              <dt className="text-xs text-muted-foreground">{f.label}</dt>
              <dd className={f.is_set ? 'mt-1 text-[13px] font-medium break-words' : 'mt-1 text-[13px] text-muted-foreground'}>{shownValue(f, tr)}</dd>
              {f.is_set && f.source ? (
                <dd className="mt-0.5 text-xs text-muted-foreground">
                  {tr(`extra.sources.${f.source}`)}
                  {f.set_by_type ? ` · ${tr(`extra.by.${f.set_by_type}`)}` : ''}
                  {f.set_at ? ` · ${formatDateTime(f.set_at)}` : ''}
                </dd>
              ) : null}
            </div>
          ))}
        </dl>
      </Section>
      {editing ? (
        <EditDialog
          detail={detail}
          fields={fields}
          zone={zone}
          onClose={() => setEditing(false)}
          onChanged={() => {
            // Saved: close, say so, show the new values.
            setEditing(false);
            toast.success(t('extra.saved'));
            r.reload();
            onChanged?.();
          }}
        />
      ) : null}
    </div>
  );
}
